import { createStore, del, get, set, type UseStore } from 'idb-keyval';

/**
 * A namespaced, promise-based key/value store backed by IndexedDB.
 *
 * IndexedDB — not localStorage — because the values here are unbounded
 * (a chat thread's full message tree, tool outputs included): localStorage's
 * ~5 MB per-origin budget is exhausted by a handful of tool-heavy threads,
 * after which every `setItem` throws `QuotaExceededError` and nothing
 * persists. IndexedDB's quota is disk-scale, its API is async (so large
 * writes never block the main thread), and it stores structured clones
 * directly — no `JSON.stringify` round-trip.
 *
 * A thin wrapper over idb-keyval's free functions so callers name a
 * database + object store once and get a typed handle, rather than
 * threading a `UseStore` through every call site.
 */
export type IdbKvStore = {
  get<ValueType>(key: string): Promise<ValueType | undefined>;
  set(key: string, value: unknown): Promise<void>;
  del(key: string): Promise<void>;
};

export function createIdbKvStore(dbName: string, storeName: string): IdbKvStore {
  const store: UseStore = createStore(dbName, storeName);
  return {
    get: <ValueType>(key: string) => get<ValueType>(key, store),
    set: (key: string, value: unknown) => set(key, value, store),
    del: (key: string) => del(key, store),
  };
}
